/**
 * FilterPopover
 *
 * Shared chrome for portal-based filter dropdowns used in the EWS dashboard
 * (balance filter on CustomersTab, risk-level filter on EWSCustomersTable
 * column header). Owns the portal div, position-fixed placement, white panel
 * styling, border, shadow, and the high-stacking z-index that escapes the
 * sticky-table thead context.
 *
 * The trigger (button shape, label, chip styling) lives at the call site
 * because the two consumers have meaningfully different specs. This component
 * is content-agnostic: children get rendered inside the floating panel.
 *
 * Pairs with `usePortalPopover` for the open/close/position state.
 *
 * Usage:
 *   const { open, pos, triggerRef, panelRef, toggle, close } = usePortalPopover();
 *   <>
 *     <button ref={triggerRef} onClick={toggle}>...</button>
 *     <FilterPopover open={open} pos={pos} panelRef={panelRef}>
 *       ...form / list / whatever
 *     </FilterPopover>
 *   </>
 */
import React, { type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { PortalPopoverPos } from '../../hooks/usePortalPopover';

/**
 * Has to exceed the table's sticky-header z-index (and any drawer overlay
 * the popover might appear above). One source of truth — please don't add
 * more hand-tuned z-indexes; raise this here if a new surface needs it.
 */
export const FILTER_POPOVER_Z = 99999;

export interface FilterPopoverProps {
  open: boolean;
  pos: PortalPopoverPos | null;
  panelRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
  /** Optional min-width override (default 140px — fits the risk-level list). */
  minWidth?: number;
}

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  pos,
  panelRef,
  children,
  minWidth = 140,
}) => {
  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: FILTER_POPOVER_Z,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        minWidth,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
